// src/pages/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import CountryDiffPanel from '../components/countries/CountryDiffPanel';
import AppSettingsPanel from '../components/settings/AppSettingsPanel';
import CatalogSyncPanel from '../components/settings/CatalogSyncPanel';

// Ayarlar sayfasının sekmeleri
const TABS = [
  {
    key: 'api-keys',
    label: 'API Anahtarları',
    title: 'API Anahtarları',
    description: 'E-SIM tedarikçileri ve diğer servisler için API anahtarlarını yönetin.',
  },
  {
    key: 'country-diff',
    label: 'KeepGo Ülke Farkları',
    title: 'KeepGo Ülke Farkları',
    description: 'KeepGo coverage\'ı ile veritabanımız arasındaki eksik ülkeleri görün ve tek tek ekleyin.',
  },
  {
    key: 'app-settings',
    label: 'Uygulama Ayarları',
    title: 'Uygulama Ayarları',
    description: 'Sistemin genel davranışını etkileyen ayarları görüntüleyin ve düzenleyin.',
  },
  {
    key: 'catalog-sync',
    label: 'Katalog Senkronu',
    title: 'Katalog Senkronu',
    description: 'Sağlayıcıdaki yeni paketleri kataloga ekleyin. Önce önizleme gösterilir.',
  },
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('api-keys');
  // API Keys yönetimi için state'ler
  const [apiKeys, setApiKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);

  // Yeni API Key form state'leri
  const [newApiKey, setNewApiKey] = useState({
    serviceName: '',
    apiKey: '',
    accessToken: '',
    baseUrl: ''
  });

  // Edit form state'leri
  const [editForm, setEditForm] = useState({
    apiKey: '',
    accessToken: '',
    baseUrl: ''
  });

  // Show/Hide state'leri
  const [showSecrets, setShowSecrets] = useState({});

  // API Keys'leri çekme
  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api-keys');

      // API response yapısını kontrol et ve array'e çevir
      let apiKeysData = [];
      if (response.data && Array.isArray(response.data)) {
        apiKeysData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        apiKeysData = response.data.data;
      } else if (response.data && typeof response.data === 'object') {
        // Eğer object ise, key'leri array'e çevir
        apiKeysData = Object.values(response.data);
      }
      
      setApiKeys(apiKeysData);
    } catch (error) {
      console.error('API Keys yüklenirken hata:', error);
      toast.error('API Keys yüklenirken bir hata oluştu');
      
      // Test için mock data (API hazır olmadığında)
      const mockData = [
        {
          serviceName: 'keepgo',
          apiKey: 'pk_live_xxxxxxxxxxxxxx',
          accessToken: 'sk_live_yyyyyyyyyyyyyy',
          baseUrl: 'https://api.keepgo.com/v2',
          isActive: true
        },
        {
          serviceName: 'airalo',
          apiKey: 'pk_test_aaaaaaaaaaaaaa',
          accessToken: 'sk_test_bbbbbbbbbbbbbb',
          baseUrl: 'https://api.airalo.com/v1',
          isActive: false
        }
      ];
      setApiKeys(mockData);
    } finally {
      setIsLoading(false);
    }
  };

  // API Keys yönetimi fonksiyonları
  const handleNewApiKeyChange = (field, value) => {
    setNewApiKey(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleShowSecret = (serviceName) => {
    setShowSecrets(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const handleAddApiKey = async (e) => {
    e.preventDefault();
    if (!newApiKey.serviceName || !newApiKey.apiKey || !newApiKey.accessToken || !newApiKey.baseUrl) {
      toast.error('Tüm alanları doldurun');
      return;
    }

    setIsSaving(true);
    try {
      await apiClient.post('/api-keys', newApiKey);
      toast.success('API Key başarıyla eklendi');
      setShowAddForm(false);
      setNewApiKey({ serviceName: '', apiKey: '', accessToken: '', baseUrl: '' });
      fetchApiKeys();
    } catch (error) {
      console.error('API Key eklenirken hata:', error);
      toast.error('API Key eklenirken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateApiKey = async (serviceName) => {
    if (!editForm.apiKey || !editForm.accessToken || !editForm.baseUrl) {
      toast.error('Tüm alanları doldurun');
      return;
    }

    setIsSaving(true);
    try {
      await apiClient.put(`/api-keys/${serviceName}`, editForm);
      toast.success('API Key başarıyla güncellendi');
      setEditingKey(null);
      setEditForm({ apiKey: '', accessToken: '', baseUrl: '' });
      fetchApiKeys();
    } catch (error) {
      console.error('API Key güncellenirken hata:', error);
      toast.error('API Key güncellenirken bir hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateApiKey = async (serviceName) => {
    try {
      await apiClient.put(`/api-keys/${serviceName}/activate`);
      toast.success('API Key başarıyla aktifleştirildi');
      fetchApiKeys();
    } catch (error) {
      console.error('API Key aktifleştirilirken hata:', error);
      toast.error('API Key aktifleştirilirken bir hata oluştu');
    }
  };

  const handleDeactivateApiKey = async (serviceName) => {
    if (window.confirm('Bu API Key\'i deaktif etmek istediğinizden emin misiniz?')) {
      try {
        await apiClient.delete(`/api-keys/${serviceName}`);
        toast.success('API Key başarıyla deaktif edildi');
        fetchApiKeys();
      } catch (error) {
        console.error('API Key deaktif edilirken hata:', error);
        toast.error('API Key deaktif edilirken bir hata oluştu');
      }
    }
  };

  const startEditing = (apiKey) => {
    setEditingKey(apiKey.serviceName);
    setEditForm({
      apiKey: apiKey.apiKey,
      accessToken: apiKey.accessToken,
      baseUrl: apiKey.baseUrl
    });
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditForm({ apiKey: '', accessToken: '', baseUrl: '' });
  };

  const activeMeta = TABS.find((tab) => tab.key === activeTab) || TABS[0];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{activeMeta.title}</h1>
          <p className={styles.description}>{activeMeta.description}</p>
        </div>
      </div>

      <div className={styles.tabRow}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'country-diff' && <CountryDiffPanel />}

      {activeTab === 'app-settings' && <AppSettingsPanel />}

      {activeTab === 'catalog-sync' && <CatalogSyncPanel />}

      {activeTab === 'api-keys' && (isLoading ? (
        <div className={styles.loadingContainerFullPage}>
          <LoadingSpinner />
          <p>Ayarlar yükleniyor...</p>
        </div>
      ) : (
      <div className={styles.section}>
        {/* Güvenlik Uyarısı */}
        <div className={styles.securityWarning}>
          <div className={styles.warningIcon}>⚠️</div>
          <div className={styles.warningContent}>
            <h3>⚠️ Güvenlik Uyarısı</h3>
            <p>
              <strong>API Keys ve Access Token'lar çok hassas bilgilerdir!</strong> Bu bilgiler:
            </p>
            <ul>
              <li>E-SIM tedarikçilerinize tam erişim sağlar</li>
              <li>Finansal işlemler yapabilir</li>
              <li>Müşteri verilerine erişebilir</li>
              <li>Yanlış ellere geçerse ciddi güvenlik riski oluşturur</li>
            </ul>
            <p className={styles.warningNote}>
              <strong>Önemli:</strong> Bu bilgileri kimseyle paylaşmayın, güvenli bir şekilde saklayın ve 
              şüpheli bir durum fark ettiğinizde hemen değiştirin.
            </p>
          </div>
        </div>

        <div className={styles.keysBlock}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>API Anahtarları</h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={showAddForm ? styles.cancelButton : styles.addButton}
          >
            {showAddForm ? 'İptal' : '+ Yeni API Key Ekle'}
          </button>
        </div>

        {/* Yeni API Key Ekleme Formu */}
        {showAddForm && (
          <div className={styles.addForm}>
            <h3>Yeni API Key Ekle</h3>
            <form onSubmit={handleAddApiKey}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Servis Adı:</label>
                  <input
                    type="text"
                    value={newApiKey.serviceName}
                    onChange={(e) => handleNewApiKeyChange('serviceName', e.target.value)}
                    placeholder="Örn: keepgo, airalo"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Base URL:</label>
                  <input
                    type="url"
                    value={newApiKey.baseUrl}
                    onChange={(e) => handleNewApiKeyChange('baseUrl', e.target.value)}
                    placeholder="https://api.service.com/v1"
                    required
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>API Key:</label>
                  <input
                    type="password"
                    value={newApiKey.apiKey}
                    onChange={(e) => handleNewApiKeyChange('apiKey', e.target.value)}
                    placeholder="API Key"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Access Token:</label>
                  <input
                    type="password"
                    value={newApiKey.accessToken}
                    onChange={(e) => handleNewApiKeyChange('accessToken', e.target.value)}
                    placeholder="Access Token"
                    required
                  />
                </div>
              </div>
              <div className={styles.formActions}>
                <button type="submit" disabled={isSaving} className={styles.saveButton}>
                  {isSaving ? 'Ekleniyor...' : 'API Key Ekle'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* API Keys Listesi */}
        <div className={styles.apiKeysList}>
          {!Array.isArray(apiKeys) || apiKeys.length === 0 ? (
            <div className={styles.emptyState}>
              <p>Henüz API Key eklenmemiş.</p>
            </div>
          ) : (
            apiKeys.map((apiKey) => (
              <div key={apiKey.serviceName} className={styles.apiKeyCard}>
                <div className={styles.apiKeyHeader}>
                  <h3>{apiKey.serviceName}</h3>
                  <div className={`${styles.statusBadge} ${apiKey.isActive ? styles.active : styles.inactive}`}>
                    {apiKey.isActive ? 'Aktif' : 'Pasif'}
                  </div>
                </div>
                
                <div className={styles.apiKeyDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>Base URL:</span>
                    {apiKey.baseUrl ? (
                      <span className={`${styles.value} num`}>{apiKey.baseUrl}</span>
                    ) : (
                      <span className={styles.valueEmpty}>—</span>
                    )}
                  </div>
                  
                  {editingKey === apiKey.serviceName ? (
                    // Edit Form
                    <div className={styles.editForm}>
                      <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                          <label>API Key:</label>
                          <input
                            type="password"
                            value={editForm.apiKey}
                            onChange={(e) => handleEditFormChange('apiKey', e.target.value)}
                            placeholder="API Key"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Access Token:</label>
                          <input
                            type="password"
                            value={editForm.accessToken}
                            onChange={(e) => handleEditFormChange('accessToken', e.target.value)}
                            placeholder="Access Token"
                          />
                        </div>
                      </div>
                      <div className={styles.formGroup}>
                        <label>Base URL:</label>
                        <input
                          type="url"
                          value={editForm.baseUrl}
                          onChange={(e) => handleEditFormChange('baseUrl', e.target.value)}
                          placeholder="Base URL"
                        />
                      </div>
                      <div className={styles.editActions}>
                        <button
                          onClick={() => handleUpdateApiKey(apiKey.serviceName)}
                          disabled={isSaving}
                          className={styles.saveButton}
                        >
                          {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className={styles.cancelButton}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Display Mode
                    <div className={styles.secretFields}>
                      <div className={styles.secretField}>
                        <span className={styles.label}>API Key:</span>
                        <div className={styles.secretInput}>
                          <input
                            type={showSecrets[apiKey.serviceName] ? 'text' : 'password'}
                            value={apiKey.apiKey}
                            readOnly
                          />
                          <button
                            onClick={() => toggleShowSecret(apiKey.serviceName)}
                            className={styles.showHideButton}
                          >
                            {showSecrets[apiKey.serviceName] ? 'Gizle' : 'Göster'}
                          </button>
                        </div>
                      </div>
                      <div className={styles.secretField}>
                        <span className={styles.label}>Access Token:</span>
                        <div className={styles.secretInput}>
                          <input
                            type={showSecrets[apiKey.serviceName] ? 'text' : 'password'}
                            value={apiKey.accessToken}
                            readOnly
                          />
                          <button
                            onClick={() => toggleShowSecret(apiKey.serviceName)}
                            className={styles.showHideButton}
                          >
                            {showSecrets[apiKey.serviceName] ? 'Gizle' : 'Göster'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.apiKeyActions}>
                  {editingKey !== apiKey.serviceName && (
                    <>
                      <button
                        onClick={() => startEditing(apiKey)}
                        className={styles.editButton}
                      >
                        Düzenle
                      </button>
                      {apiKey.isActive ? (
                        <button
                          onClick={() => handleDeactivateApiKey(apiKey.serviceName)}
                          className={styles.deactivateButton}
                        >
                          Deaktif Et
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivateApiKey(apiKey.serviceName)}
                          className={styles.activateButton}
                        >
                          Aktif Et
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        </div>
      </div>
      ))}
    </div>
  );
};

export default SettingsPage;